/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useQuery } from 'react-query';
import { isMobile } from 'react-device-detect';
import { currencies, exchange } from '../../../../utilities/constants';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import { LOAN_DETAILS } from '../../queries';
import { LOAN_STATUS, MODAL_SCREENS } from '../../constants';
import { FLOWS, sendStats } from '../../../../services/mixpanel';
import { MXP_FTC_REPAYMENT_VISIT, MXP_FTC_REPAYMENT_CONTINUE } from '../../mixpanel-events';
import { getLoanDetailsHelper } from '../../helpers';
import { useUserPersonalInfo } from '../../../../utilities/sharedHooks/user';

const LoanDetails = ({ loanMetaData, showScreen, allLoans }) => {
  const { t: translate } = useTranslation();
  const { data: loan, isLoading } = useQuery([LOAN_DETAILS, loanMetaData.id]);
  useEffect(() => {
    sendStats(MXP_FTC_REPAYMENT_VISIT, {}, FLOWS.FTC);
  }, []);
  const currentLoad = allLoans.find((l) => loanMetaData.id === l.id);
  const { isCollateralNeeded, isInterestsOverdue } = loanMetaData;
  const principalCurrency = loanMetaData.principal_currency.toUpperCase();
  const collateralCurrency = loanMetaData.collateral_currency.toUpperCase();
  const rate = useRate(collateralCurrency, principalCurrency);
  const shouldRenderFooter = currentLoad.status === LOAN_STATUS.ACTIVE;

  const { isUS } = useUserPersonalInfo();

  const loanData = useMemo(() => {
    if (!isLoading && loan) {
      const availableBalance = loan.net_principal_amount * currencies[principalCurrency].conversion;
      const collateralAmount = loan.collateral_amount * currencies[collateralCurrency].conversion;

      const collateralAmountFiat = rate * collateralAmount;

      const duration = parseInt(loan.duration_days / 30, 10);

      const initialLtvPercentage = loan.ltv_percentage_initial;
      const currentLtvPercentage = loan.ltv_percentage_current;
      const interestApr = loan.interest_apr;
      const interestAmount = formatNumber(
        loan.interest_amount,
        currencies[principalCurrency].decimal
      );

      const principalAmount = loan.principal_amount * currencies[principalCurrency].conversion;

      const totalPayment = parseFloat(interestAmount) + parseFloat(principalAmount);

      const dueDate = moment(loan.loan_due_at).format(isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY');
      const updatedAt = moment(loan.updated_at).format(isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY');
      const settledAt = moment(currentLoad.settled_at).format(isUS ? 'MM/DD/YYYY' : 'DD/MM/YYYY');

      return {
        principalCurrency,
        collateralCurrency,
        availableBalance,
        collateralAmount,
        collateralAmountFiat,
        duration,
        initialLtvPercentage,
        currentLtvPercentage,
        interestApr,
        interestAmount,
        dueDate,
        updatedAt,
        settledAt,
        totalPayment,
        ...loan,
      };
    }
    return {
      principalCurrency: '-',
      collateralCurrency: '-',
      availableBalance: '-',
      collateralAmount: '-',
      collateralAmountFiat: '-',
      duration: '-',
      initialLtvPercentage: '-',
      currentLtvPercentage: '-',
      interestApr: '-',
      interestAmount: '-',
      dueDate: '-',
      totalPayment: '-',
    };
  }, [isLoading]);

  const { formattedCurrentLTV: currentLTV } = useMemo(
    () => getLoanDetailsHelper(loanMetaData, rate),
    [loanMetaData, rate]
  );

  const renderFooter = () => (
    <button
      className="btn btn-primary mb-0 mb-md-5"
      type="button"
      onClick={() => {
        sendStats(MXP_FTC_REPAYMENT_CONTINUE, {}, FLOWS.FTC);
        if (isInterestsOverdue) {
          showScreen(MODAL_SCREENS.PAY_INTEREST, { loanMetaData, loanData });
          return;
        }
        showScreen(MODAL_SCREENS.REPAY_LOAN, { loanMetaData, loanData });
      }}>
      {translate('loans_details_button_label')}
    </button>
  );

  return (
    <ModalBodyLayout
      withBack={false}
      title={translate('loans_details_title_label')}
      renderFooter={shouldRenderFooter ? renderFooter : null}
      className="pb-4"
      scrollStyle={isMobile ? { maxHeight: '31.5rem' } : false}>
      <div>
        <div className="flex-row-center">
          <div>
            <h2 className="text-primary m-0">
              {loanData.availableBalance} {principalCurrency}
            </h2>
            <p className="text-shadow text-f6">{translate('loan_pay_interest_note')}</p>
          </div>
          <div>
            <h2 className="m-0">
              {translate('loans_payment_duration_label').replace('%s', loanData.duration)}
            </h2>
            <p className="text-shadow text-f6">{translate('loans_payment_duration_sub_label')}</p>
          </div>
        </div>
        {isCollateralNeeded && (
          <div
            className="alert alert-danger alert-action cursor-pointer mt-4 py-3 mb-0"
            role="button"
            tabIndex={-1}
            onClick={() => showScreen(MODAL_SCREENS.LOAN_MORE_COLLATERAL)}
            onKeyDown={() => showScreen(MODAL_SCREENS.LOAN_MORE_COLLATERAL)}>
            <i className="text-danger icon icon-cancel-icon pe-none" />
            <span className="content text-start pe-none">
              <p className="m-0">{translate('loan_details_more_collateral_needed_warning')}</p>
            </span>

            <i className="icon icon-arrow-next-navigation ms-auto pe-none text-danger" />
          </div>
        )}
        <div className="rounded-extra px-3 py-3 bg-light flex-row-center mt-4">
          <p className="text-f5 text-shadow">{translate('loans_payment_total_collateral_label')}</p>
          <div className="text-right">
            <p className="text-f5 text-dark">
              <b>
                {`${formatNumber(
                  loanData.collateralAmount,
                  currencies[collateralCurrency].decimal
                )} ${collateralCurrency}`}
              </b>
            </p>
            <p className="text-f6 text-shadow">
              {`${formatNumberWithComma(
                formatNumber(loanData.collateralAmountFiat, exchange.FIAT_VALUE)
              )} ${principalCurrency}`}
            </p>
          </div>
        </div>
        <div className="text-f6 text-shadow text-start" style={{ marginTop: '0.75rem' }}>
          <p>{translate('loans_payment_collateral_note')}</p>
        </div>
        {isInterestsOverdue && (
          <div
            className="alert alert-info alert-action cursor-pointer mt-4 py-3 mb-0"
            onClick={() => showScreen(MODAL_SCREENS.PAY_INTEREST, { loanMetaData, loanData })}
            onKeyDown={() => {}}
            role="button"
            tabIndex={-1}>
            <i className="text-info icon icon-info-icon pe-none" />
            <span className="content text-start pe-none">
              <p className="m-0">{translate('loan_interest_add_info')}</p>
              <p className="m-0">{translate('loan_interest_add_info_1')}</p>
            </span>
            <i className="icon icon-arrow-next-navigation ms-auto pe-none text-info" />
          </div>
        )}
        <div className="rounded-extra bg-light mt-4">
          <div className="flex-row-center px-3 py-3 border-bottom border-disabled">
            <p className="text-f6 text-shadow">{translate('loans_agreed_ltv_label')}</p>
            <b className="text-f6">{loanData.initialLtvPercentage}%</b>
          </div>
          <div className="flex-row-center px-3 py-3 border-bottom border-disabled">
            <p className="text-f6 text-shadow">{translate('loans_interest_label')}</p>
            <b className="text-f6">{loanData.interestApr}%</b>
          </div>
          <div className="flex-row-center px-3 py-3">
            <p className="text-f6 text-shadow">{translate('loans_total_interest_label')}</p>
            <b className="text-f6 text-uppercase">
              {formatNumberWithComma(
                formatNumber(loanData.interestAmount, currencies[principalCurrency].decimal)
              )}{' '}
              {principalCurrency}
            </b>
          </div>
        </div>
        <div className="rounded-extra bg-light mt-4">
          <div className="flex-row-center px-3 py-3 border-bottom border-disabled">
            <p className="text-f6 text-shadow">{translate('loans_ltv_market_label')}</p>
            <b className="text-f6">{currentLTV}%</b>
          </div>
          <div className="flex-row-center px-3 py-3">
            <p className="text-f6 text-shadow">{translate('loans_total_payments_label')}</p>
            <span className="text-end">
              <b className="text-f6">
                {formatNumberWithComma(
                  formatNumber(loanData.totalPayment, currencies[principalCurrency].decimal)
                )}{' '}
                {principalCurrency}
              </b>
              <p className="text-f6 text-shadow">
                {translate('loans_principal_interest_sublabel')}
              </p>
            </span>
          </div>
        </div>
        <div className="rounded-extra bg-light mt-4">
          <div className="flex-row-center px-3 py-3">
            <p className="text-f6 text-shadow">{translate('loans_pay_off_date_label')}</p>
            <b className="text-f6">{`${
              currentLoad.status === LOAN_STATUS.SETTLED ? loanData.settledAt : loanData.dueDate
            }`}</b>
          </div>
        </div>
        <div className="text-f6 text-shadow text-start mt-2 mb-4" style={{ marginTop: '0.75rem' }}>
          <p>{translate('loans_payment_due_date_note')}</p>
        </div>
      </div>
    </ModalBodyLayout>
  );
};

export default LoanDetails;

LoanDetails.propTypes = {
  loanMetaData: PropTypes.object,
  showScreen: PropTypes.func.isRequired,
  allLoans: PropTypes.arrayOf(PropTypes.object).isRequired,
};
LoanDetails.defaultProps = {
  loanMetaData: null,
};
